'use client';

import dayjs from 'dayjs';
import useSWR from 'swr';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';

import { paths } from 'src/routes/paths';
import { NyttFärgTema, ChipFärger } from 'src/theme/NyttFärgtema';
import { HämtaMatchningarAvLöner } from 'src/sections/loner/LoneService';
import type { LöneResponse, IMatchningAvLön } from 'src/types/nya/ekonomi/loner/LoneTyper';
import { useAppContext } from '@/src/context/app';

import type { KortProps } from '../kort-katalog';
import DomainCard from '../components/domain-card';
import { MiniList, MiniRow, formatKr, formatSignedKr, swrOptions } from './kort-primitiver';

// ----------------------------------------------------------------------

export default function LönKort({ orgId, räkenskapsår }: KortProps) {
    const { organisation } = useAppContext();

    const startKey = räkenskapsår?.förstaDag
        ? String((räkenskapsår.förstaDag as any).format?.('YYYY-MM-DD') ?? räkenskapsår.förstaDag)
        : '';

    const { data, isLoading } = useSWR<LöneResponse>(
        orgId ? ['loner-matchningar', orgId, startKey] : null,
        () => HämtaMatchningarAvLöner(organisation ?? null, räkenskapsår ?? null),
        swrOptions
    );

    const matchningar = data?.matchningarAvLöner ?? [];
    const theme = NyttFärgTema.Kombinationer[2];
    const href = paths.dashboard.ekonomi.loner.root;

    if (isLoading && matchningar.length === 0) {
        return (
            <DomainCard title="Lön" icon="solar:wallet-money-bold" theme={theme} href={href}
                primaryMetric={{ value: '—', label: 'utbetalt i år' }}>
                <Stack spacing={0.75}>
                    <Skeleton variant="text" height={20} />
                    <Skeleton variant="text" height={20} />
                </Stack>
            </DomainCard>
        );
    }

    if (matchningar.length === 0) {
        return (
            <DomainCard title="Lön" icon="solar:wallet-money-bold" theme={theme} href={href}
                emptyMessage="Inga lönehändelser för räkenskapsåret ännu." />
        );
    }

    const utbetalt = (m: IMatchningAvLön) =>
        Math.abs(m.valdaBanktransaktioner?.reduce((s, t) => s + (t.belopp ?? 0), 0) ?? 0);
    const totalUtbetalt = matchningar.reduce((s, m) => s + utbetalt(m), 0);
    const bokförda = matchningar.filter((m) => m.bokförd).length;
    const antal = matchningar.length;

    const lönSorteringsvärde = (m: IMatchningAvLön) => {
        const start = m.arbetsgivardeklaration?.period?.förstaDag;
        const d = start ? dayjs(start as any) : null;
        return d && d.isValid()
            ? d.valueOf()
            : Number(String(m.arbetsgivardeklaration?.redovisningsperiod ?? '').replace(/\D/g, '')) || 0;
    };
    const senaste = [...matchningar]
        .sort((a, b) => lönSorteringsvärde(b) - lönSorteringsvärde(a))
        .slice(0, 3);

    return (
        <DomainCard
            title="Lön"
            icon="solar:wallet-money-bold"
            theme={theme}
            href={href}
            primaryMetric={{ value: formatKr(totalUtbetalt), label: 'utbetalt i år' }}
            secondaryMetric={{ value: `${antal}`, label: antal === 1 ? 'lönekörning' : 'lönekörningar' }}
            statusChip={{ label: `${bokförda}/${antal} BOKFÖRDA`, color: ChipFärger[2] }}
        >
            <MiniList>
                {senaste.map((m) => (
                    <MiniRow
                        key={m.id}
                        primary={m.arbetsgivardeklaration?.redovisningsperiod || 'Lönekörning'}
                        secondary={m.bokförd ? 'Bokförd' : 'Ej bokförd'}
                        trailing={formatSignedKr(-utbetalt(m))}
                    />
                ))}
            </MiniList>
        </DomainCard>
    );
}
